import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";

import { Table, Select, QuickFilterTypes } from "@cofano/cck";

import { TableId, useFrontendTable } from "components/table";

import { DataFormat } from "imports/Utils";

import { isLocaleDE, isLocaleEN, isLocaleNL } from "utils/locales";

import { headerMap } from "view/products/utils/ProductUtils";
import { productCargoTankDesignField } from "view/products/components/form/fields/ProductCargoTankDesign";
import { productCargoTankTypeField } from "view/products/components/form/fields/ProductCargoTankType";
import { productCargoTankEquipmentField } from "view/products/components/form/fields/ProductCargoTankEquipment";
import { productSamplingTypeField } from "view/products/components/form/fields/ProductSamplingType";
import { productTankerTypeField } from "view/products/components/form/fields/ProductTankerType";
import { productTemperatureClassField } from "view/products/components/form/fields/ProductTemperatureClass";
import { productEquipmentField } from "view/products/components/form/fields/ProductEquipment";

import { AdnGoodFragment } from "__generated__/view/products/graphql/product.fragments.generated";

interface ProductsAdnTableProps {
	content: AdnGoodFragment[];
	contentLoading: boolean;
	onRowClick: (row: AdnGoodFragment) => void;
}

const ProductsAdnTable = ({ content, contentLoading, onRowClick }: ProductsAdnTableProps) => {

	const { locale } = useStoreState(store => store.session);

	const { tableProps } = useFrontendTable({
		id: TableId.ProductsAdn,
		content: content,
		onCustomFilter: (property) => {
			if (property === "unNumber") {
				return (field, value) => {
					return field && value ? (field.toString()).startsWith(value.toString()) : false;
				};
			}

			if (property === "packingGroup") {
				return (field, value) => {
					return field && value ? (field.toLowerCase() === value.toLowerCase()) : false;
				};
			}
		},
	});

	return (
		<Table
			{...tableProps}
			contentLoading={contentLoading}
			onRowClick={row => onRowClick(row as AdnGoodFragment)}
			columns={[ {
				id: "adnCode",
				title: intl.formatMessage({ id: "PRODUCT_ADN_ADN_CODE" }),
				sortable: true,
				filterComponent: QuickFilterTypes.NUMBER,
			}, {
				id: "unNumber",
				title: headerMap["unNumber"],
				sortable: true,
				filterComponent: QuickFilterTypes.NUMBER,
			}, {
				id: "properShippingNameEN",
				title: headerMap["properShippingNameEN"],
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				width: 200,
			}, {
				id: "properShippingNameNL",
				title: headerMap["properShippingNameNL"],
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				width: 200,
			}, {
				id: "properShippingNameDE",
				title: headerMap["properShippingNameDE"],
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				width: 200,
			}, {
				id: "properShippingNameFR",
				title: headerMap["properShippingNameFR"],
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				width: 200,
			}, {
				id: "descriptionNL",
				title: intl.formatMessage({ id: "PRODUCT_ADN_DESCRIPTION_NL" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				disabled: !isLocaleNL(locale),
				width: 200,
				defaultVisible: false,
			}, {
				id: "descriptionEN",
				title: intl.formatMessage({ id: "PRODUCT_ADN_DESCRIPTION_EN" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				disabled: !isLocaleEN(locale),
				width: 200,
				defaultVisible: false,
			}, {
				id: "descriptionDE",
				title: intl.formatMessage({ id: "PRODUCT_ADN_DESCRIPTION_DE" }),
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				disabled: !isLocaleDE(locale),
				width: 200,
				defaultVisible: false,
			}, {
				id: "classNumber",
				title: headerMap["classNumber"],
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "classificationCode",
				title: headerMap["classificationCode"],
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				width: 170,
			}, {
				id: "packingGroup",
				title: headerMap["packingGroup"],
				sortable: true,
				filterComponent: (value, onChange) => (
					<Select
						label={headerMap["packingGroup"]}
						value={value}
						onChange={onChange}
						fetchOptions={[ "I", "II", "III" ]} />
				),
				width: 170,
			}, {
				id: "hazards",
				title: headerMap["hazards"],
				sortable: true,
				filterComponent: QuickFilterTypes.TEXT,
				width: 150,
			}, {
				id: "tankerType",
				title: headerMap["tankerType"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
				render: (row) => productTankerTypeField(row.tankerType),
			}, {
				id: "cargoTankDesign",
				title: headerMap["cargoTankDesign"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
				render: (row) => productCargoTankDesignField(row.cargoTankDesign),
			}, {
				id: "cargoTankType",
				title: headerMap["cargoTankType"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
				render: (row) => productCargoTankTypeField(row.cargoTankType),
			}, {
				id: "cargoTankEquipment",
				title: headerMap["cargoTankEquipment"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
				render: (row) => productCargoTankEquipmentField(row.cargoTankEquipment),
			}, {
				id: "openingPressureRelieveValve",
				title: headerMap["openingPressureRelieveValve"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "maxFillingDegreePercentage",
				title: headerMap["maxFillingDegreePercentage"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "density",
				title: headerMap["density"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "densityTemperature",
				title: headerMap["densityTemperature"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.NUMBER,
			}, {
				id: "samplingType",
				title: headerMap["samplingType"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
				render: (row) => productSamplingTypeField(row.samplingType),
			}, {
				id: "permissiblePumpChamberUnderDeck",
				title: headerMap["permissiblePumpChamberUnderDeck"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.BOOLEAN,
				render: (row, field) => DataFormat.boolean(field),
			}, {
				id: "temperatureClass",
				title: headerMap["temperatureClass"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
				render: (row) => productTemperatureClassField(row.temperatureClass),
			}, {
				id: "explosionGroup",
				title: headerMap["explosionGroup"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
			}, {
				id: "explosionProtectionRequired",
				title: headerMap["explosionProtectionRequired"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.BOOLEAN,
				render: (row, field) => DataFormat.boolean(field),
			}, {
				id: "requiredEquipment",
				title: headerMap["requiredEquipment"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
				render: (row) => productEquipmentField(row.requiredEquipment),
			}, {
				id: "numberOfBlueConesOrLights",
				title: headerMap["numberOfBlueConesOrLights"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.NUMBER,
			}, {
				id: "extraRequirements",
				title: headerMap["extraRequirements"],
				defaultVisible: false,
				filterComponent: QuickFilterTypes.TEXT,
			} ]} />
	);
};

export default ProductsAdnTable;
